import type { FieldManifest } from "../../../shared/api/generated/contracts";
import { useLocale, useT } from "../../../shared/i18n";
import { Field } from "../../../shared/ui";
import {
  isSupportedControl,
  type FieldControl as ControlKind,
  type FieldLabels,
  type FieldMode,
} from "./contracts";
import { FIELD_RENDERERS } from "./renderers";

export interface FieldControlProps {
  field: FieldManifest;
  value: unknown;
  mode: FieldMode;
  error?: string;
  onChange?: (value: unknown) => void;
}

/**
 * Renders one manifest field in the requested mode. Edit mode wraps the control
 * in a Field so the label, the required marker and the server diagnostic travel
 * with it; display and print modes emit the bare value.
 */
export function FieldControl({
  field,
  value,
  mode,
  error,
  onChange,
}: FieldControlProps) {
  const t = useT();
  const { locale } = useLocale();
  if (!isSupportedControl(field))
    return <div role="alert">{t("widget.unsupportedElement")}</div>;
  const render = FIELD_RENDERERS[field.control as ControlKind];
  const labels: FieldLabels = {
    choose: t("field.choose"),
    chooseDate: t("field.chooseDate"),
    chooseTime: t("field.chooseTime"),
    remove: t("field.remove"),
    empty: t("field.empty"),
  };
  if (mode !== "edit")
    return <>{render({ field, value, mode, labels, locale })}</>;
  return (
    <Field
      label={field.label}
      error={error}
      required={field.required}
      requiredLabel={t("record.required")}
    >
      {(fieldProps) =>
        render({
          field,
          value,
          mode,
          error,
          onChange,
          labels,
          locale,
          ...fieldProps,
        })
      }
    </Field>
  );
}
